import {
	CopyOutlined,
	DeleteOutlined,
	EditOutlined,
	NotificationOutlined,
	PlusOutlined,
} from '@ant-design/icons'
import {
	Button,
	Input,
	Popconfirm,
	Select,
	Space,
	Table,
	Tag,
	Tooltip,
	message,
} from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import { ROUTE } from '../../utils/constant'
import './AnnouncementList.scss'

const STATUS_COLORS = {
	draft: 'default',
	scheduled: 'processing',
	live: 'success',
	archived: 'warning',
}

const PRIORITY_COLORS = {
	critical: 'red',
	feature: 'blue',
	tip: 'cyan',
}

const PLATFORM_COLORS = {
	web: 'geekblue',
	ios: 'purple',
	android: 'green',
}

const SURFACE_LABELS = {
	modal_carousel: 'What’s New popup',
	home_banner: 'Home banner',
	changelog: 'Help → What’s new',
}

const createPath = `${ROUTE.ANNOUNCEMENT_MANAGEMENT}${ROUTE.CREATE}`

export const AnnouncementList = () => {
	const history = useHistory()
	const [items, setItems] = useState([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [page, setPage] = useState(0)
	const [pageSize, setPageSize] = useState(20)
	const [status, setStatus] = useState(undefined)
	const [platform, setPlatform] = useState(undefined)
	const [priority, setPriority] = useState(undefined)
	const [query, setQuery] = useState('')

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const rs = await api.announcementAdminApi.list({
				page,
				pageSize,
				status,
				platform,
				priority,
				q: query.trim() || undefined,
			})
			setItems(Array.isArray(rs?.data) ? rs.data : [])
			setTotal(Number(rs?.total) || 0)
		} catch (err) {
			message.error(err?.message || 'Could not load announcements.')
		} finally {
			setLoading(false)
		}
	}, [page, pageSize, status, platform, priority, query])

	useEffect(() => {
		load()
	}, [load])

	const handleDelete = async id => {
		try {
			await api.announcementAdminApi.remove(id)
			message.success('Announcement deleted.')
			load()
		} catch (err) {
			message.error(err?.message || 'Could not delete announcement.')
		}
	}

	const handleDuplicate = async id => {
		try {
			const copy = await api.announcementAdminApi.duplicate(id)
			message.success('Duplicated as draft.')
			history.push(`${createPath}?id=${copy.id}`)
		} catch (err) {
			message.error(err?.message || 'Could not duplicate announcement.')
		}
	}

	const columns = [
		{
			title: 'Title',
			key: 'title',
			ellipsis: true,
			render: (_, record) =>
				record.locales?.vi?.title ||
				record.locales?.en?.title ||
				'—',
		},
		{
			title: 'Slug',
			dataIndex: 'slug',
			key: 'slug',
			ellipsis: true,
			width: 180,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 110,
			render: value => (
				<Tag color={STATUS_COLORS[value] || 'default'}>{value}</Tag>
			),
		},
		{
			title: 'Priority',
			dataIndex: 'priority',
			key: 'priority',
			width: 100,
			render: value => (
				<Tag color={PRIORITY_COLORS[value] || 'default'}>{value}</Tag>
			),
		},
		{
			title: 'Platforms',
			dataIndex: 'platforms',
			key: 'platforms',
			width: 200,
			render: platforms =>
				(platforms || []).map(p => (
					<Tag key={p} color={PLATFORM_COLORS[p] || 'default'}>
						{p}
					</Tag>
				)),
		},
		{
			title: 'Where',
			dataIndex: 'surfaces',
			key: 'surfaces',
			ellipsis: true,
			render: surfaces =>
				(surfaces || [])
					.map(s => SURFACE_LABELS[s] || s)
					.join(', ') || '—',
		},
		{
			title: 'Window',
			key: 'window',
			width: 200,
			render: (_, record) => {
				const start = record.startsAt
					? new Date(record.startsAt).toLocaleDateString()
					: '—'
				const end = record.endsAt
					? new Date(record.endsAt).toLocaleDateString()
					: '—'
				return `${start} → ${end}`
			},
		},
		{
			title: 'Updated',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			width: 160,
			render: value => (value ? new Date(value).toLocaleString() : '—'),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 220,
			fixed: 'right',
			render: (_, record) => (
				<Space size={4}>
					<Button
						size="small"
						icon={<EditOutlined />}
						onClick={() =>
							history.push(`${createPath}?id=${record.id}`)
						}
					>
						Edit
					</Button>
					<Tooltip title="Duplicate as draft">
						<Button
							size="small"
							icon={<CopyOutlined />}
							onClick={() => handleDuplicate(record.id)}
						/>
					</Tooltip>
					<Popconfirm
						title="Delete this announcement?"
						okText="Delete"
						okButtonProps={{ danger: true }}
						onConfirm={() => handleDelete(record.id)}
					>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	]

	return (
		<ListPageShell className="announcement-list">
			<ListPageIntro
				icon={NotificationOutlined}
				title="Product announcements"
				subtitle="Manage What’s New popup, Home banner, and Help entries for web & mobile."
			/>
			<div className="announcement-list__toolbar">
				<Space wrap>
					<Input.Search
						allowClear
						placeholder="Search title, slug, link"
						onSearch={value => {
							setPage(0)
							setQuery(value || '')
						}}
						style={{ width: 260 }}
					/>
					<Select
						allowClear
						placeholder="Status"
						style={{ width: 140 }}
						value={status}
						onChange={value => {
							setPage(0)
							setStatus(value)
						}}
						options={[
							{ value: 'draft', label: 'Draft' },
							{ value: 'scheduled', label: 'Scheduled' },
							{ value: 'live', label: 'Live' },
							{ value: 'archived', label: 'Archived' },
						]}
					/>
					<Select
						allowClear
						placeholder="Platform"
						style={{ width: 130 }}
						value={platform}
						onChange={value => {
							setPage(0)
							setPlatform(value)
						}}
						options={[
							{ value: 'web', label: 'Web' },
							{ value: 'ios', label: 'iOS' },
							{ value: 'android', label: 'Android' },
						]}
					/>
					<Select
						allowClear
						placeholder="Priority"
						style={{ width: 130 }}
						value={priority}
						onChange={value => {
							setPage(0)
							setPriority(value)
						}}
						options={[
							{ value: 'critical', label: 'Critical' },
							{ value: 'feature', label: 'Feature' },
							{ value: 'tip', label: 'Tip' },
						]}
					/>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => history.push(createPath)}
					>
						Create announcement
					</Button>
				</Space>
			</div>
			<Table
				rowKey="id"
				loading={loading}
				columns={columns}
				dataSource={items}
				scroll={{ x: 1200 }}
				pagination={{
					current: page + 1,
					pageSize,
					total,
					showSizeChanger: true,
					showTotal: t => `${t} announcement(s)`,
					onChange: (nextPage, nextSize) => {
						setPage(nextPage - 1)
						setPageSize(nextSize)
					},
				}}
			/>
		</ListPageShell>
	)
}
